import { SUPABASE_URL, SUPABASE_KEY, SESSION_KEY } from './constants.js';

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const auth = {
  signIn: async (email, password) => {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: { 'apikey': SUPABASE_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error_description || data.message || 'Login failed');
    return data;
  },
  signOut: async (token) => {
    try {
      await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_KEY, 'Authorization': `Bearer ${token}` },
      });
    } catch (e) {}
  },
  refresh: async (refreshToken) => {
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { 'apikey': SUPABASE_KEY, 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.access_token ? data : null;
  },
  getRole: async (userId, token) => {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/staff_roles?user_id=eq.${userId}&active=eq.true&select=*&limit=1`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': `Bearer ${token}` } }
    );
    const rows = await res.json();
    return Array.isArray(rows) ? rows[0] || null : null;
  },
  // Marketplace: a non-staff login may be a vendor (vendor_read_own RLS policy)
  getVendor: async (userId, token) => {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/vendors?user_id=eq.${userId}&select=*&limit=1`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': `Bearer ${token}` } }
    );
    const rows = await res.json();
    return Array.isArray(rows) ? rows[0] || null : null;
  },
};

// ─── TOKEN REFRESH HELPER ─────────────────────────────────────────────────────
// Returns a valid access_token, refreshing it silently if it's about to expire.
export const getValidToken = async () => {
  const stored = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  if (!stored?.access_token) return null;
  // Refresh proactively if expiring within 5 minutes
  if (stored.expires_at && Date.now() > stored.expires_at - 300000 && stored.refresh_token) {
    try {
      const data = await auth.refresh(stored.refresh_token);
      if (data) {
        localStorage.setItem(SESSION_KEY, JSON.stringify({
          ...stored,
          access_token:  data.access_token,
          refresh_token: data.refresh_token || stored.refresh_token,
          expires_at:    Date.now() + ((data.expires_in || 3600) - 60) * 1000,
        }));
        return data.access_token;
      }
      // Refresh failed — wipe session so AdminShell can log out
      localStorage.removeItem(SESSION_KEY);
      return null;
    } catch (e) { /* network issue — return existing token best-effort */ }
  }
  return stored.access_token;
};

// ─── SUPABASE FETCH WRAPPER ───────────────────────────────────────────────────
// Always returns { data, totalCount }.
//   data       — parsed response body (array for collections, object for single row)
//   totalCount — parsed from Content-Range header (e.g. "0-49/1234" → 1234),
//                null when the header is absent (non-paginated requests)
//
// Pass extraHeaders to supply Range + Prefer: count=exact for paginated queries.
export const supaFetch = async (method, table, body = null, query = '', extraHeaders = {}) => {
  const _t = await getValidToken() || SUPABASE_KEY;
  const headers = {
    'Content-Type': 'application/json',
    'apikey': SUPABASE_KEY,
    'Authorization': `Bearer ${_t}`,
    'Prefer': 'return=representation',
    ...extraHeaders,          // caller may override Prefer and add Range
  };
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}${query}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  if (!res.ok) throw new Error(text);

  // Parse Content-Range for total count — Supabase returns "0-49/1234"
  const contentRange = res.headers.get('Content-Range');
  let totalCount = null;
  if (contentRange && contentRange.includes('/')) {
    totalCount = parseInt(contentRange.split('/')[1], 10);
  }

  const data = text ? JSON.parse(text) : [];
  return { data, totalCount };
};

// ─── DB HELPERS ───────────────────────────────────────────────────────────────
// Each method unwraps .data from supaFetch so callers throughout the app
// continue to receive plain arrays/objects (backward-compatible API).
export const db = {
  getAll:  async (table)         => (await supaFetch('GET',   table, null,  '?order=created_at.desc')).data,
  insert:  async (table, row)    => (await supaFetch('POST',  table, row)).data,
  update:  async (table, id, row)=> (await supaFetch('PATCH', table, row,   `?id=eq.${id}`)).data,
  delete:  async (table, id)     => (await supaFetch('DELETE',table, null,  `?id=eq.${id}`)).data,

  getSetting: async (key) => {
    const { data: rows } = await supaFetch('GET', 'settings', null, `?key=eq.${key}`);
    return rows[0]?.value || '';
  },
  setSetting: async (key, value) => {
    const { data: existing } = await supaFetch('GET', 'settings', null, `?key=eq.${key}`);
    if (existing.length > 0) {
      return (await supaFetch('PATCH', 'settings', { value }, `?key=eq.${key}`)).data;
    } else {
      return (await supaFetch('POST',  'settings', { key, value })).data;
    }
  },

  upsertProducts: async (rows) => (await supaFetch('POST', 'products', rows, '')).data,

  // Fast card-level fetch — skips heavy images JSONB + description
  getCards: async (table) => (await supaFetch(
    'GET', table, null,
    '?select=id,name_en,name_ar,sku,category,price,min_qty,stock,image,description_ar&order=category.asc,name_en.asc'
  )).data,

  // Single-row fetch with all columns (lazy product detail)
  getById: async (table, id) =>
    (await supaFetch('GET', table, null, `?id=eq.${encodeURIComponent(id)}&select=*`)).data,
};
